import type { PasswordPolicyDescriptor } from "./first-credential-client";

/**
 * One-read in-memory fc1 handoff (CLMS-137 T023). The redeemed authorization
 * crosses from the registration page to /set-password only through this
 * module-level slot owned by the still-mounted SPA root: destructive
 * ownership is taken exactly once, every later read returns null, and
 * nothing ever touches browser storage, cookies, the URL, or history
 * state — so reload, hard navigation, new tab, session restore, and crash
 * all lose it by construction and the generic lost-session re-entry state
 * renders instead.
 *
 * Two-phase consumption (invitation-proof-fragment pattern): the page
 * PEEKS non-destructively during render — React-router v7 wraps
 * navigations in startTransition, so a render of /set-password can be
 * started and abandoned without committing, and a destructive render-phase
 * read would consume the handoff with no committed UI to show for it. Only
 * the COMMIT-phase claim (a mount's first effect, presenting the ticket the
 * peek minted) takes destructive ownership. An abandoned render's staging
 * is simply replaced when the next mount peeks fresh, so its stale ticket
 * can never claim.
 *
 * Deadline backstop (invitation-proof-fragment pattern): a
 * staged-but-never-COMMITTED peek (store → peek → abandoned render) must
 * not retain the bearer in module memory indefinitely while waiting for a
 * later call that may never come. Staging records an absolute 30s expiry
 * and arms a generation-scoped self-scrub timer (cancel-then-arm; a stale
 * generation no-ops). Because browser timers only guarantee a minimum
 * delay — a throttled/backgrounded tab can deliver the callback
 * arbitrarily late — the deadline is ALSO enforced synchronously on every
 * peek/claim/commit: an expired staging is scrubbed fail-closed (holder
 * fields nulled in place, stored slot cleared) before the caller observes
 * anything. A COMMITTED claim is owned by the page lifecycle and never
 * expires under the live form: commit cancels the timer and clears the
 * deadline.
 *
 * Stored-slot absolute deadline (T036 review observation (a)): a
 * stored-but-never-PEEKED handoff (navigation to /set-password interrupted
 * before first render) must not retain the bearer in module memory until
 * page unload. Storing records an absolute 60s deadline and arms a
 * generation-scoped self-scrub timer with the same synchronous access-time
 * enforcement as the staging deadline: every peek/claim/commit scrubs an
 * expired stored slot fail-closed before observing anything. The deadline
 * persists through staging (the module's total in-memory bearer lifetime
 * stays bounded) and is cleared only when the handoff is consumed (commit
 * or legacy claim) or dropped.
 *
 * Dual-clock deadlines (T036 review): Date.now() is non-monotonic — a
 * backward system-clock jump would otherwise make a deadline unreachable
 * and the stored secret effectively immortal. Every deadline is therefore
 * recorded against BOTH clocks: performance.now() (monotonic, primary) and
 * Date.now() (wall clock, belt-and-suspenders — it catches a suspended
 * monotonic clock across sleep). A slot expires when EITHER clock passes
 * its deadline.
 */

export interface FirstCredentialHandoff {
  authorization: string;
  expiresAt: string;
  passwordPolicy: PasswordPolicyDescriptor;
}

/**
 * Stable mutable holder handed to the page: both React fiber buffers
 * reference this SAME object, so scrubFirstCredentialHandoffHolder's
 * in-place field nulling makes the secret unreachable from the current AND
 * the alternate fiber immediately — no follow-up commit required.
 */
export interface FirstCredentialHandoffHolder {
  authorization: string | null;
  expiresAt: string | null;
  passwordPolicy: PasswordPolicyDescriptor | null;
}

/**
 * Opaque, unforgeable per-peek claim capability. Only the mount that
 * performed the staging peek (and holds this ticket in a fiber-local ref)
 * can re-read the staged holder or take destructive ownership at commit.
 */
export type FirstCredentialClaimTicket = symbol;

export interface FirstCredentialHandoffPeek {
  holder: FirstCredentialHandoffHolder | null;
  ticket: FirstCredentialClaimTicket | null;
}

/**
 * Abandoned-render bound for an UNCOMMITTED staging: if the mount that
 * peeked never commits (aborted concurrent render, error boundary), the
 * staged bearer self-scrubs after this window instead of persisting until
 * some later module call.
 */
export const UNCOMMITTED_STAGING_SELF_SCRUB_MS = 30_000;

/**
 * Absolute in-memory bound for a STORED handoff, peeked or not (T036 (a)).
 * 60s: the happy path consumes the handoff within milliseconds of
 * navigation (peek on first render, claim on first effect), so a minute
 * comfortably covers slow devices while staying far below the server-side
 * 15-minute authority ceiling.
 */
export const STORED_HANDOFF_DEADLINE_MS = 60_000;

// Cumulative armed-time witness (T036 round 3, finding 2): browser timers
// never fire EARLY, so a delivered callback is independent proof that at
// least its armed delay of real time elapsed — regardless of what either
// clock reports. Each backstop-timer generation tracks the TOTAL delay it
// has been armed for; a delivery whose cumulative armed delay has reached
// the full configured deadline scrubs immediately, so under a compound
// clock stall (performance.now() frozen AND the wall clock regressed) the
// secret's in-memory lifetime is bounded at exactly the configured
// deadline instead of being stretched by a reschedule budget. A delivery
// below the full deadline (spurious early delivery) re-checks the clocks
// and re-arms for the monotonic remainder (clamped to ≥1ms), the
// remainder ADDING to the cumulative armed time.

let heldHandoff: FirstCredentialHandoff | null = null;
let stagedHolder: FirstCredentialHandoffHolder | null = null;
let stagedTicket: FirstCredentialClaimTicket | null = null;
let stagedClaimCommitted = false;

/**
 * Absolute deadlines for the CURRENT uncommitted staging (wall clock and
 * monotonic — either expires it); null once committed (page-lifecycle
 * owned) or when nothing is staged. The deadlines are authoritative — the
 * timer below is best-effort eager cleanup.
 */
let stagedDeadline: number | null = null;
let stagedMonotonicDeadline: number | null = null;

/**
 * Absolute deadlines for the CURRENT stored handoff (wall clock and
 * monotonic — either expires it); set at store, persist through staging,
 * cleared only on consumption (commit or legacy claim) or drop.
 * Authoritative — the stored timer is best-effort eager cleanup, like the
 * staging timer.
 */
let storedDeadline: number | null = null;
let storedMonotonicDeadline: number | null = null;

/**
 * Generation token for the stored-slot backstop timer: bumped on every
 * store so a stale callback (from a replaced store — even one re-storing
 * the SAME object instance) can never scrub a newer generation. The timer
 * closure captures only this number, never the secret-bearing handoff.
 */
let storedGeneration = 0;

let selfScrubTimer: ReturnType<typeof setTimeout> | null = null;
let storedScrubTimer: ReturnType<typeof setTimeout> | null = null;

function cancelSelfScrubTimer(): void {
  if (selfScrubTimer !== null) {
    clearTimeout(selfScrubTimer);
    selfScrubTimer = null;
  }
}

function cancelStoredScrubTimer(): void {
  if (storedScrubTimer !== null) {
    clearTimeout(storedScrubTimer);
    storedScrubTimer = null;
  }
}

/** True when either clock (monotonic primary, wall-clock backup) has passed its deadline. */
function isDeadlinePassed(wallDeadline: number | null, monotonicDeadline: number | null): boolean {
  return (
    (monotonicDeadline !== null && performance.now() >= monotonicDeadline) ||
    (wallDeadline !== null && Date.now() >= wallDeadline)
  );
}

function isStagingExpired(): boolean {
  return (
    stagedHolder !== null &&
    !stagedClaimCommitted &&
    isDeadlinePassed(stagedDeadline, stagedMonotonicDeadline)
  );
}

function isStoredExpired(): boolean {
  return heldHandoff !== null && isDeadlinePassed(storedDeadline, storedMonotonicDeadline);
}

/**
 * Shared expiry for EITHER deadline — an uncommitted staging past its 30s
 * window or a stored handoff past its 60s absolute bound (access-time
 * enforcement and both self-scrub timers). Fail closed: the secret-bearing
 * state is nulled FIRST, before anything else runs.
 */
function expireHandoff(): void {
  heldHandoff = null;
  clearStoredDeadline();
  resetStaging();
}

function clearStoredDeadline(): void {
  storedDeadline = null;
  storedMonotonicDeadline = null;
  cancelStoredScrubTimer();
}

/** Scrubs a holder's fields in place so no retained reference (any fiber buffer) can reach the secret. */
export function scrubFirstCredentialHandoffHolder(
  holder: FirstCredentialHandoffHolder | null,
): void {
  if (holder === null) {
    return;
  }
  holder.authorization = null;
  holder.expiresAt = null;
  holder.passwordPolicy = null;
}

function resetStaging(): void {
  scrubFirstCredentialHandoffHolder(stagedHolder);
  stagedHolder = null;
  stagedTicket = null;
  stagedClaimCommitted = false;
  stagedDeadline = null;
  stagedMonotonicDeadline = null;
  cancelSelfScrubTimer();
}

/**
 * Arms the stored-slot backstop timer. The closure captures ONLY the
 * generation token and the cumulative armed delay — never the
 * secret-bearing handoff object — so a stale callback can neither scrub a
 * newer generation (even one re-storing the same object instance) nor
 * retain the secret. A delivery whose cumulative armed delay has reached
 * STORED_HANDOFF_DEADLINE_MS scrubs unconditionally (armed-time witness —
 * see the module note above): the timer being armed for that total delay
 * proves the deadline elapsed even when both clocks are stalled. A
 * spurious earlier delivery re-checks the absolute deadline (mirroring the
 * synchronous access-time enforcement) and re-arms for the remaining
 * monotonic time, clamped to at least 1ms so a passed/garbled monotonic
 * deadline can never arm a zero or negative delay; the remainder adds to
 * the cumulative armed time.
 */
function armStoredScrubTimer(generation: number, delayMs: number, priorArmedMs: number): void {
  const cumulativeArmedMs = priorArmedMs + delayMs;
  storedScrubTimer = setTimeout(() => {
    if (generation !== storedGeneration || heldHandoff === null) {
      return;
    }
    storedScrubTimer = null;
    if (cumulativeArmedMs < STORED_HANDOFF_DEADLINE_MS && !isStoredExpired()) {
      const remainingMs =
        storedMonotonicDeadline === null
          ? 1
          : Math.max(storedMonotonicDeadline - performance.now(), 1);
      armStoredScrubTimer(generation, remainingMs, cumulativeArmedMs);
      return;
    }
    expireHandoff();
  }, delayMs);
}

/**
 * Arms the staging backstop timer, mirroring the stored-slot timer's
 * armed-time witness, early-delivery re-check, and clamped delay: a
 * delivery whose cumulative armed delay has reached
 * UNCOMMITTED_STAGING_SELF_SCRUB_MS scrubs unconditionally even under a
 * compound clock stall. Ticket identity plays the generation-token role: a
 * stale callback (replaced staging, commit, clear) no-ops.
 */
function armStagedScrubTimer(
  ticket: FirstCredentialClaimTicket,
  delayMs: number,
  priorArmedMs: number,
): void {
  const cumulativeArmedMs = priorArmedMs + delayMs;
  selfScrubTimer = setTimeout(() => {
    if (ticket !== stagedTicket || stagedClaimCommitted) {
      return;
    }
    selfScrubTimer = null;
    if (cumulativeArmedMs < UNCOMMITTED_STAGING_SELF_SCRUB_MS && !isStagingExpired()) {
      const remainingMs =
        stagedMonotonicDeadline === null
          ? 1
          : Math.max(stagedMonotonicDeadline - performance.now(), 1);
      armStagedScrubTimer(ticket, remainingMs, cumulativeArmedMs);
      return;
    }
    expireHandoff();
  }, delayMs);
}

/**
 * BFCache scrub (T036 round 2, finding 3; spec: browser restore renders
 * the lost-session state). BFCache back-navigation never unmounts React,
 * so module memory — including a COMMITTED holder — would survive the
 * restore. pagehide is the last event before a page enters the cache:
 * scrub ALL module state there, unconditionally (persisted or not — on a
 * real unload the memory dies anyway, so the unconditional scrub is safe
 * and covers both). Client-side SPA route navigation never fires pagehide,
 * so the normal store → navigate → peek flow is unaffected. Registered
 * lazily on the first store/stage so module import stays side-effect free;
 * never unregistered (one idempotent listener for the page lifetime).
 */
let pagehideScrubRegistered = false;

function registerPagehideScrub(): void {
  if (pagehideScrubRegistered || typeof window === "undefined") {
    return;
  }
  pagehideScrubRegistered = true;
  window.addEventListener("pagehide", () => {
    clearFirstCredentialHandoff();
  });
}

/** Replaces any unclaimed handoff with the newest store (still one-read); a stale staging is scrubbed. */
export function storeFirstCredentialHandoff(handoff: FirstCredentialHandoff): void {
  registerPagehideScrub();
  resetStaging();
  heldHandoff = handoff;
  // Absolute stored-slot bound (T036 (a)): dual-clock deadline (monotonic
  // primary, wall-clock belt-and-suspenders) plus a generation-scoped
  // self-scrub timer (cancel-then-arm keeps at most one; a stale generation
  // — replaced, consumed, cleared — no-ops via the generation token).
  cancelStoredScrubTimer();
  storedGeneration += 1;
  storedDeadline = Date.now() + STORED_HANDOFF_DEADLINE_MS;
  storedMonotonicDeadline = performance.now() + STORED_HANDOFF_DEADLINE_MS;
  armStoredScrubTimer(storedGeneration, STORED_HANDOFF_DEADLINE_MS, 0);
}

/**
 * Legacy single-phase destructive read: returns the handoff exactly once,
 * deleting it on read; null afterwards. Consuming here also scrubs and
 * invalidates any staged-but-uncommitted peek (one-read: only one consumer
 * ever obtains the secret).
 */
export function claimFirstCredentialHandoff(): FirstCredentialHandoff | null {
  if (isStoredExpired() || isStagingExpired()) {
    expireHandoff();
    return null;
  }
  const claimed = heldHandoff;
  heldHandoff = null;
  clearStoredDeadline();
  if (claimed !== null) {
    resetStaging();
  }
  return claimed;
}

/**
 * Render-phase NON-destructive peek. Only a TICKET-LESS peek (a new mount's
 * first render) may create or replace staging: it stages the stored handoff
 * into a fresh holder and mints the claim ticket, scrubbing any abandoned
 * staging so its ticket can no longer claim — the stored slot is untouched,
 * so an abandoned render never loses the handoff for the next committed
 * mount. Repeated peeks presenting the CURRENT ticket (StrictMode
 * double-invoked initializer on the same fiber) return the SAME holder. A
 * non-null stale/foreign ticket fails closed with { holder: null, ticket:
 * null } — it must never restage, scrub a newer holder, or mint itself a
 * fresh ticket. Once the handoff has been consumed (commit claim or legacy
 * claim), ticket-less peeks return null/empty.
 */
export function peekFirstCredentialHandoff(
  ticket: FirstCredentialClaimTicket | null = null,
): FirstCredentialHandoffPeek {
  // Synchronous deadline enforcement (throttled tabs deliver timers late):
  // an expired stored handoff or uncommitted staging is scrubbed before
  // anything observes it.
  if (isStoredExpired() || isStagingExpired()) {
    expireHandoff();
    return { holder: null, ticket: null };
  }
  if (ticket !== null && ticket === stagedTicket && stagedHolder !== null) {
    return { holder: stagedHolder, ticket };
  }
  // Fail closed for a stale/foreign ticket: only a ticket-less peek (a new
  // mount's first render) may create or replace staging. An abandoned fiber
  // retried after a newer mount staged must not scrub the newer holder or
  // mint itself a fresh ticket.
  if (ticket !== null) {
    return { holder: null, ticket: null };
  }
  if (heldHandoff === null) {
    return { holder: null, ticket: null };
  }
  registerPagehideScrub();
  resetStaging();
  const minted: FirstCredentialClaimTicket = Symbol("first-credential-claim");
  stagedHolder = {
    authorization: heldHandoff.authorization,
    expiresAt: heldHandoff.expiresAt,
    passwordPolicy: heldHandoff.passwordPolicy,
  };
  stagedTicket = minted;
  // Backstop for a staging whose mount never commits: dual-clock absolute
  // deadline plus a ticket-scoped self-scrub timer (cancel-then-arm keeps
  // at most one; a stale ticket — newer staging, commit, clear — no-ops),
  // with the same armed-time witness and early-delivery re-check as the
  // stored-slot timer.
  stagedDeadline = Date.now() + UNCOMMITTED_STAGING_SELF_SCRUB_MS;
  stagedMonotonicDeadline = performance.now() + UNCOMMITTED_STAGING_SELF_SCRUB_MS;
  armStagedScrubTimer(minted, UNCOMMITTED_STAGING_SELF_SCRUB_MS, 0);
  return { holder: stagedHolder, ticket: minted };
}

/**
 * Commit-phase destructive claim: only after this does the module slot
 * forget the handoff. Succeeds only for the CURRENTLY staged ticket, and is
 * idempotent for that owning ticket (StrictMode's dev double-effect
 * re-presents it and must remain owner). A stale/foreign/null ticket fails.
 */
export function commitFirstCredentialHandoffClaim(
  ticket: FirstCredentialClaimTicket | null,
): boolean {
  // Synchronous deadline enforcement mirrors peek: a commit arriving after
  // either deadline expired fails closed (scrub first, then refuse).
  if (isStoredExpired() || isStagingExpired()) {
    expireHandoff();
    return false;
  }
  if (ticket === null || ticket !== stagedTicket || stagedHolder === null) {
    return false;
  }
  if (stagedClaimCommitted) {
    return true;
  }
  if (heldHandoff === null) {
    return false;
  }
  heldHandoff = null;
  stagedClaimCommitted = true;
  // A committed claim is owned by the page lifecycle — it must never expire
  // under the live form.
  stagedDeadline = null;
  stagedMonotonicDeadline = null;
  cancelSelfScrubTimer();
  clearStoredDeadline();
  return true;
}

/** Drops an unclaimed handoff and scrubs any staged holder (terminal states, tests). */
export function clearFirstCredentialHandoff(): void {
  heldHandoff = null;
  clearStoredDeadline();
  resetStaging();
}

/**
 * Test-only unscoped peek at the staged holder so suites can assert the
 * in-place scrub. Enforces both absolute deadlines exactly like the real
 * access paths, so it can never be used to observe a secret past expiry.
 */
export function peekFirstCredentialHandoffHolderForTests(): FirstCredentialHandoffHolder | null {
  if (isStoredExpired() || isStagingExpired()) {
    expireHandoff();
    return null;
  }
  return stagedHolder;
}
